'use client';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  setRoutesTempLabel,
  addRoutesTempStop,
  removeRoutesTempStop,
  clearDirections,
  startCreateMode,
  startEditMode,
  startViewMode,
  cancelRouteEdit,
  toggleRouteVisibility,
  reorderRoutesTempStops,
} from 'src/redux';
import type { EnrichedRouteStop } from './types';

interface StartEditViewParams {
  routeId: string;
  label: string;
  stops: EnrichedRouteStop[];
}

export interface UseRouteActionsReturn {
  startCreate: () => void;
  startEdit: (params: {
    routeId: string;
    label: string;
    stops: EnrichedRouteStop[];
  }) => void;
  startView: (params: {
    routeId: string;
    label: string;
    stops: EnrichedRouteStop[];
  }) => void;
  setLabel: (label: string) => void;
  addStop: (stop: EnrichedRouteStop) => void;
  removeStop: (stopId: string) => void;
  reorderStops: (stops: EnrichedRouteStop[]) => void;
  cancel: () => void;
  clearDirections: () => void;
  toggleRouteVisibility: (routeId: string) => void;
}

const useRouteActions = (): UseRouteActionsReturn => {
  const dispatch = useDispatch();

  const startCreate = useCallback(() => {
    dispatch(startCreateMode());
  }, [dispatch]);

  const startEdit = useCallback(
    (params: StartEditViewParams) => {
      dispatch(startEditMode(params));
    },
    [dispatch]
  );

  const startView = useCallback(
    (params: StartEditViewParams) => {
      dispatch(startViewMode(params));
    },
    [dispatch]
  );

  const setLabel = useCallback(
    (label: string) => {
      dispatch(setRoutesTempLabel(label));
    },
    [dispatch]
  );

  const addStop = useCallback(
    (stop: EnrichedRouteStop) => {
      dispatch(addRoutesTempStop(stop));
    },
    [dispatch]
  );

  const removeStop = useCallback(
    (stopId: string) => {
      dispatch(removeRoutesTempStop(stopId));
    },
    [dispatch]
  );

  const reorderStops = useCallback(
    (stops: EnrichedRouteStop[]) => {
      dispatch(reorderRoutesTempStops(stops));
    },
    [dispatch]
  );

  const cancel = useCallback(() => {
    dispatch(cancelRouteEdit());
  }, [dispatch]);

  const clearDirectionsAction = useCallback(() => {
    dispatch(clearDirections());
  }, [dispatch]);

  const toggleVisibility = useCallback(
    (routeId: string) => {
      dispatch(toggleRouteVisibility(routeId));
    },
    [dispatch]
  );

  return {
    startCreate,
    startEdit,
    startView,
    setLabel,
    addStop,
    removeStop,
    reorderStops,
    cancel,
    clearDirections: clearDirectionsAction,
    toggleRouteVisibility: toggleVisibility,
  };
};

export default useRouteActions;
